import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Campaign as CampaignIcon,
  Construction as ConstructionIcon,
  Timeline as MonitorIcon,
  MoreVert as MoreVertIcon,
  RestartAlt as RestartIcon,
  Code as CodeIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import RouterMonitorModal from './RouterMonitorModal';
import { useAuth } from '../../contexts/AuthContext';

const RouterForm = ({ open, onClose, onSubmit, router = null, zones = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    ip: '',
    username: '',
    password: '',
    zone_id: '',
    api_port: '8728',
    api_type: 'classic',
    rest_port: '80',
    description: '',
    outage_alerts_enabled: true,
    outage_message: 'Estimado/a {client_name}, le informamos que actualmente detectamos una interrupción en el servicio de Internet en el sector {sector_nombre}. Nuestro equipo técnico ya está trabajando para solucionar el inconveniente lo antes posible. Agradecemos su comprensión.',
    recovery_message: 'Estimado/a {client_name}, nos complace informarle que el servicio de Internet en el sector {sector_nombre} ha sido restablecido con éxito y está funcionando con normalidad. Gracias por su paciencia.',
    ip_subnets: '',
    client_interface: ''
  });

  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (router) {
      setFormData({
        name: router.name || '',
        ip: router.ip || '',
        username: router.username || '',
        password: router.password || '',
        zone_id: router.zone_id || '',
        api_port: router.api_port || '8728',
        api_type: router.api_type || 'classic',
        rest_port: router.rest_port || '80',
        description: router.description || '',
        outage_alerts_enabled: router.outage_alerts_enabled !== undefined ? !!router.outage_alerts_enabled : true,
        outage_message: router.outage_message || 'Estimado/a {client_name}, le informamos que actualmente detectamos una interrupción en el servicio de Internet en el sector {sector_nombre}. Nuestro equipo técnico ya está trabajando para solucionar el inconveniente lo antes posible. Agradecemos su comprensión.',
        recovery_message: router.recovery_message || 'Estimado/a {client_name}, nos complace informarle que el servicio de Internet en el sector {sector_nombre} ha sido restablecido con éxito y está funcionando con normalidad. Gracias por su paciencia.',
        ip_subnets: router.ip_subnets || '',
        client_interface: router.client_interface || ''
      });
      setTestResult(null);
    } else {
      setFormData({
        name: '',
        ip: '',
        username: '',
        password: '',
        zone_id: '',
        api_port: '8728',
        api_type: 'classic',
        rest_port: '80',
        description: '',
        outage_alerts_enabled: true,
        outage_message: 'Estimado/a {client_name}, le informamos que actualmente detectamos una interrupción en el servicio de Internet en el sector {sector_nombre}. Nuestro equipo técnico ya está trabajando para solucionar el inconveniente lo antes posible. Agradecemos su comprensión.',
        recovery_message: 'Estimado/a {client_name}, nos complace informarle que el servicio de Internet en el sector {sector_nombre} ha sido restablecido con éxito y está funcionando con normalidad. Gracias por su paciencia.',
        ip_subnets: '',
        client_interface: ''
      });
      setTestResult(null);
    }
  }, [router, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Resetear el resultado de la prueba solo si no se están editando templates
    if (name !== 'outage_alerts_enabled' && name !== 'outage_message' && name !== 'recovery_message') {
      setTestResult(null);
    }
  };

  // En el componente RouterForm
  const testConnection = async () => {
    setTesting(true);
    setError('');
    try {
      const response = await fetch('/api/routers/test-connection', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ip: formData.ip,
          username: formData.username,
          password: formData.password,
          api_port: formData.api_port,
          api_type: formData.api_type,
          rest_port: formData.rest_port
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al probar la conexión');
      }

      setTestResult(true);
    } catch (error) {
      setTestResult(false);
      setError(error.message || 'Error al probar la conexión');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!testResult) {
      setError('Debe probar la conexión antes de guardar');
      return;
    }
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {router ? 'Editar Router' : 'Nuevo Router'}
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4 py-4">
            {error && (
              <Alert severity="error" className="mb-4">
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Nombre del Router"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              select
              label="Zona"
              name="zone_id"
              value={formData.zone_id}
              onChange={handleChange}
              required
            >
              {zones.map(zone => (
                <MenuItem key={zone.id} value={zone.id}>
                  {zone.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Dirección IP"
              name="ip"
              value={formData.ip}
              onChange={handleChange}
              required
              helperText="Si el router es sin IP Pública (VPN), usa el rango 10.8.0.x (ej: 10.8.0.4, 10.8.0.5)."
            />

            <TextField
              fullWidth
              label="Puerto API (Clásico)"
              name="api_port"
              value={formData.api_port}
              onChange={handleChange}
              type="number"
              required
              helperText="Puerto TCP de la API clásica (default: 8728)"
            />

            <TextField
              fullWidth
              select
              label="Tipo de API"
              name="api_type"
              value={formData.api_type}
              onChange={handleChange}
              helperText="Selecciona cómo se conectará el sistema al router"
            >
              <MenuItem value="classic">Clásica (TCP 8728) — RouterOS v6/v7</MenuItem>
              <MenuItem value="rest">REST API (HTTP) — RouterOS v7+</MenuItem>
              <MenuItem value="rest-ssl">REST API (HTTPS) — RouterOS v7+ con SSL</MenuItem>
            </TextField>

            {(formData.api_type === 'rest' || formData.api_type === 'rest-ssl') && (
              <TextField
                fullWidth
                label={`Puerto REST (${formData.api_type === 'rest-ssl' ? 'HTTPS' : 'HTTP'})`}
                name="rest_port"
                value={formData.rest_port}
                onChange={handleChange}
                type="number"
                helperText={`Puerto del servicio web del router (default: ${formData.api_type === 'rest-ssl' ? '443' : '80'})`}
              />
            )}

            <TextField
              fullWidth
              label="Usuario"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Contraseña"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Subredes de Clientes (CIDR)"
              name="ip_subnets"
              value={formData.ip_subnets}
              onChange={handleChange}
              placeholder="192.168.80.0/24, 192.168.90.0/24"
              helperText="Ejemplo: 192.168.80.0/24. Separar con comas si hay múltiples subredes."
              margin="normal"
            />

            <TextField
              fullWidth
              label="Interfaz de Clientes"
              name="client_interface"
              value={formData.client_interface}
              onChange={handleChange}
              placeholder="ether2"
              helperText="Nombre exacto de la interfaz en MikroTik donde se conectan los APs"
              margin="normal"
            />

            <Box className="border-t border-gray-100 pt-4 mt-4">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.outage_alerts_enabled}
                    onChange={handleChange}
                    name="outage_alerts_enabled"
                    color="primary"
                    type="checkbox"
                  />
                }
                label="Habilitar Alertas de Caída (WhatsApp)"
              />
            </Box>

            {formData.outage_alerts_enabled && (
              <Box className="space-y-4">
                <TextField
                  fullWidth
                  label="Mensaje de Caída de Servicio (WhatsApp)"
                  name="outage_message"
                  value={formData.outage_message}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  helperText="Variables disponibles: {client_name}, {sector_nombre}, {router_name}"
                />

                <TextField
                  fullWidth
                  label="Mensaje de Restablecimiento de Servicio (WhatsApp)"
                  name="recovery_message"
                  value={formData.recovery_message}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  helperText="Variables disponibles: {client_name}, {sector_nombre}, {router_name}"
                />
              </Box>
            )}

            <Box className="flex items-center space-x-2 pt-2">
              <Button
                variant="outlined"
                onClick={testConnection}
                disabled={testing}
                startIcon={testing ? <CircularProgress size={20} /> : <SyncIcon />}
              >
                Probar Conexión
              </Button>
              {testResult !== null && (
                <Box className="flex items-center">
                  {testResult ? (
                    <CheckIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!testResult}
          >
            Guardar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const BroadcastDialog = ({ open, onClose, router = null }) => {
  const [type, setType] = useState('outage');
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open) {
      setType('outage');
      setCustomMessage('');
      setError('');
      setSuccess('');
    }
  }, [open]);

  const handleSend = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`/api/routers/${router.id}/broadcast-alert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type,
          customMessage: type === 'custom' ? customMessage : undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al enviar la difusión');
      }

      setSuccess(`Difusión completada con éxito. Enviados: ${data.summary.successCount}, Fallados: ${data.summary.failedCount}.`);
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      setError(err.message || 'Error al enviar la difusión');
    } finally {
      setLoading(false);
    }
  };

  if (!router) return null;

  return (
    <Dialog open={open} onClose={loading ? null : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        📢 Difusión de Comunicado por WhatsApp
      </DialogTitle>
      <DialogContent>
        <Box className="space-y-4 py-4" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body1">
            Estás a punto de enviar una alerta de WhatsApp a todos los clientes <strong>activos</strong> conectados al sector <strong>{router.name}</strong> ({router.clientCount || 0} clientes).
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <FormControl component="fieldset" fullWidth disabled={loading}>
            <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>Selecciona el tipo de comunicado:</FormLabel>
            <RadioGroup
              value={type}
              onChange={(e) => setType(e.target.value)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Paper sx={{ p: 2, border: '1px solid rgba(0,0,0,0.12)' }} elevation={0}>
                <FormControlLabel 
                  value="outage" 
                  control={<Radio />} 
                  label={<Typography sx={{ fontWeight: 'bold', color: 'error.main' }}>Alerta de Caída de Servicio</Typography>} 
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', pl: 4 }}>
                  Enviará el mensaje de interrupción configurado para este router.
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, border: '1px solid rgba(0,0,0,0.12)' }} elevation={0}>
                <FormControlLabel 
                  value="recovery" 
                  control={<Radio />} 
                  label={<Typography sx={{ fontWeight: 'bold', color: 'success.main' }}>Alerta de Restablecimiento</Typography>} 
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', pl: 4 }}>
                  Enviará el mensaje de restauración configurado para este router.
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, border: '1px solid rgba(0,0,0,0.12)' }} elevation={0}>
                <FormControlLabel 
                  value="custom" 
                  control={<Radio />} 
                  label={<Typography sx={{ fontWeight: 'bold', color: 'info.main' }}>Comunicado Personalizado</Typography>} 
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', pl: 4, mb: 1 }}>
                  Permite escribir un texto único para este momento.
                </Typography>
                {type === 'custom' && (
                  <Box sx={{ pl: 4, pt: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Mensaje Personalizado"
                      placeholder="Escribe el mensaje aquí..."
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      disabled={loading}
                      helperText="Variables: {client_name}, {sector_nombre}, {router_name}"
                    />
                  </Box>
                )}
              </Paper>
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="primary"
          disabled={loading || (type === 'custom' && !customMessage.trim())}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Enviando...' : `Enviar a ${router.clientCount || 0} Clientes`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RouterSetupDialog = ({ open, onClose, router = null }) => {
  const [serverIp, setServerIp] = useState('10.8.0.1');
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [steps, setSteps] = useState([
    { id: 1, label: 'Registrar IP de Servidor en Address-List (yinowisp_server)', status: 'pending' },
    { id: 2, label: 'Purgar Reglas de Redirección NAT obsoletas', status: 'pending' },
    { id: 3, label: 'Instalar Nuevas Reglas NAT (Bypass, Corte, Aviso)', status: 'pending' },
    { id: 4, label: 'Limpiar Reglas de Filtro de Firewall obsoletas', status: 'pending' },
    { id: 5, label: 'Instalar Filtros de Excepción y Bloqueo General', status: 'pending' }
  ]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setServerIp('10.8.0.1');
      setLoading(false);
      setLogs([]);
      setSuccess(false);
      setError('');
      setSteps([
        { id: 1, label: 'Registrar IP de Servidor en Address-List (yinowisp_server)', status: 'pending' },
        { id: 2, label: 'Purgar Reglas de Redirección NAT obsoletas', status: 'pending' },
        { id: 3, label: 'Instalar Nuevas Reglas NAT (Bypass, Corte, Aviso)', status: 'pending' },
        { id: 4, label: 'Limpiar Reglas de Filtro de Firewall obsoletas', status: 'pending' },
        { id: 5, label: 'Instalar Filtros de Excepción y Bloqueo General', status: 'pending' }
      ]);
    }
  }, [open]);

  const handleRunSetup = async () => {
    setLoading(true);
    setLogs([]);
    setError('');
    setSuccess(false);
    
    // Reset steps to processing/pending
    setSteps(prev => prev.map(s => ({ ...s, status: s.id === 1 ? 'running' : 'pending' })));

    try {
      const response = await fetch(`/api/routers/${router.id}/setup-rules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ serverIp })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Error durante la auto-configuración de MikroTik');
      }

      // Animamos la aparición de logs para simular la consola retro y dar feedback visual
      const returnedLogs = data.logs || [];
      let tempLogs = [];
      
      for (let i = 0; i < returnedLogs.length; i++) {
        const logLine = returnedLogs[i];
        tempLogs.push(logLine);
        setLogs([...tempLogs]);
        
        // Evaluar qué paso se completa basado en el contenido del log
        const text = logLine.message;
        if (text.includes('yinowisp_server')) {
          setSteps(prev => prev.map(s => s.id === 1 ? { ...s, status: 'success' } : s.id === 2 ? { ...s, status: 'running' } : s));
        } else if (text.includes('reglas NAT obsoletas') || text.includes('NAT dstnat')) {
          setSteps(prev => prev.map(s => s.id === 2 ? { ...s, status: 'success' } : s.id === 3 ? { ...s, status: 'running' } : s));
        } else if (text.includes('Redirección de Aviso') || text.includes('Bypass de Aviso') || text.includes('Bypass de aviso agregada') || text.includes('Redireccion HTTP por Corte')) {
          setSteps(prev => prev.map(s => s.id === 3 ? { ...s, status: 'success' } : s.id === 4 ? { ...s, status: 'running' } : s));
        } else if (text.includes('Firewall Filter para purgar') || text.includes('Filtro: Drop') || text.includes('Filtro: DNS')) {
          setSteps(prev => prev.map(s => s.id === 4 ? { ...s, status: 'success' } : s.id === 5 ? { ...s, status: 'running' } : s));
        } else if (text.includes('FINALIZADA CON ÉXITO')) {
          setSteps(prev => prev.map(s => ({ ...s, status: 'success' })));
        }

        // Un leve retraso de 100ms para un efecto super premium en la interfaz
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Error de comunicación con el servidor');
      setSteps(prev => prev.map(s => s.status === 'running' ? { ...s, status: 'failed' } : s));
      
      // Añadir log de error en el terminal
      setLogs(prev => [...prev, {
        type: 'error',
        message: err.message || 'Error de comunicación con el servidor',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!router) return null;

  return (
    <Dialog open={open} onClose={loading ? null : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ConstructionIcon color="primary" /> Asistente de Configuración de Red — MikroTik
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body2" color="textSecondary">
            Este asistente configura automáticamente las reglas dstnat, address-lists y filtros de firewall necesarios en tu MikroTik <strong>{router.name}</strong> ({router.ip}) para el funcionamiento correcto de avisos de vencimiento y cortes.
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>IP de Redirección (VPS WireGuard Gateway)</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Servidor VPN / IP Destino"
                value={serverIp}
                onChange={(e) => setServerIp(e.target.value)}
                disabled={loading || success}
                placeholder="10.8.0.1"
                helperText="IP del VPS asignada en el túnel WireGuard (ej: 10.8.0.1)"
                fullWidth
              />
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Checklist */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>Progreso de la Instalación</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {steps.map(step => (
                  <Box key={step.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {step.status === 'success' && <CheckIcon color="success" fontSize="small" />}
                    {step.status === 'failed' && <ErrorIcon color="error" fontSize="small" />}
                    {step.status === 'running' && <CircularProgress size={18} />}
                    {step.status === 'pending' && <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(0,0,0,0.2)' }} />}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: step.status === 'success' ? 'text.primary' : step.status === 'running' ? 'primary.main' : 'text.secondary',
                        fontWeight: step.status === 'running' ? 'bold' : 'normal'
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Retro Console */}
            <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Terminal de RouterOS API</Typography>
              <Box
                sx={{
                  backgroundColor: '#1e1e1e',
                  color: '#00ff00',
                  fontFamily: 'Consolas, Monaco, monospace',
                  p: 2,
                  borderRadius: 1,
                  height: '220px',
                  overflowY: 'auto',
                  border: '1px solid #333',
                  boxShadow: 'inset 0 0 10px #000',
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#444', borderRadius: '4px' }
                }}
              >
                {logs.length === 0 && !loading && (
                  <Typography sx={{ color: '#888', fontStyle: 'italic', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    Esperando inicio de configuración...
                  </Typography>
                )}
                {logs.map((log, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontFamily: 'Consolas, Monaco, monospace',
                      fontSize: '0.8rem',
                      color: log.type === 'error' ? '#ff3333' : log.type === 'warning' ? '#ff9900' : log.type === 'success' ? '#33ff33' : '#00ff00',
                      mb: 0.5,
                      lineHeight: 1.3
                    }}
                  >
                    {`> [${new Date(log.timestamp).toLocaleTimeString()}] ${log.message}`}
                  </Typography>
                ))}
                {loading && (
                  <Typography 
                    sx={{ 
                      fontFamily: 'Consolas, Monaco, monospace', 
                      fontSize: '0.8rem', 
                      color: '#00ff00',
                      animation: 'blink 1s infinite',
                      '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0 }
                      }
                    }}
                  >
                    &gt; Enviando scripts al MikroTik... ▉
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">¡Configuración completada con éxito! Las reglas dstnat y filtros de firewall están activos en tu router.</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {success ? 'Cerrar' : 'Cancelar'}
        </Button>
        <Button
          onClick={handleRunSetup}
          variant="contained"
          color="primary"
          disabled={loading || !serverIp.trim()}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ConstructionIcon />}
        >
          {loading ? 'Instalando...' : success ? 'Reinstalar Reglas' : 'Instalar Reglas'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RouterNoIpScriptDialog = ({ open, onClose, router = null }) => {
  const [copied, setCopied] = useState(false);
  const [tunnelType, setTunnelType] = useState('sstp');
  
  if (!router) return null;

  const vpsHost = '2.24.90.63';
  const routerPort = router.api_port || 8728;
  const routerUser = router.username || 'admin';
  const routerPass = router.password || '';

  const sstpScript = `# ====================================================================
# SCRIPT DE CONEXION YINOWISP (SIN IP PUBLICA - TUNEL SSTP)
# Router: ${router.name} (IP Remota VPN: ${router.ip})
# ====================================================================

# 1. Habilitar servicio API clasico en MikroTik (Puerto ${routerPort})
/ip service set api disabled=no port=${routerPort};

# 2. Configurar cliente SSTP apuntando al VPS YinoWISP (${vpsHost})
/interface sstp-client remove [find name="yinowisp-vpn"];
/interface sstp-client add name="yinowisp-vpn" connect-to="${vpsHost}" port=443 user="${routerUser}" password="${routerPass}" profile=default-encrypted disabled=no comment="Tunel YinoWISP Sin IP Publica";

# 3. Permitir trafico API e ICMP desde el Servidor YinoWISP VPS
/ip firewall address-list remove [find list="yinowisp_server"];
/ip firewall address-list add list="yinowisp_server" address=${vpsHost} comment="Servidor YinoWISP VPS Publico";
/ip firewall address-list add list="yinowisp_server" address=10.8.0.1 comment="Gateway VPN YinoWISP";

# 4. Configurar DNS estatico local para avisos de corte
/ip dns static remove [find name="aviso.yinowisp.com"];
/ip dns static add name="aviso.yinowisp.com" address=10.8.0.1 comment="Servidor de Avisos YinoWISP";

# 5. Notificacion final
:put "=== CONEXION YINOWISP CONFIGURADA EXITOSAMENTE EN MIKROTIK ===";
`;

  const wireguardScript = `# ====================================================================
# SCRIPT DE CONEXION YINOWISP (SIN IP PUBLICA - WIREGUARD)
# Router: ${router.name} (IP Remota VPN: ${router.ip})
# ====================================================================

# 1. Habilitar servicio API clasico en MikroTik (Puerto ${routerPort})
/ip service set api disabled=no port=${routerPort};

# 2. Crear interfaz WireGuard YinoWISP
/interface wireguard remove [find name="wg-yinowisp"];
/interface wireguard add name="wg-yinowisp" listen-port=51820 comment="Tunel WireGuard YinoWISP";

# 3. Asignar direccion IP VPN al cliente MikroTik
/ip address remove [find comment="IP Tunel WireGuard YinoWISP"];
/ip address add address=${router.ip}/24 interface=wg-yinowisp comment="IP Tunel WireGuard YinoWISP";

# 4. Configurar Peer Servidor VPS YinoWISP (${vpsHost})
/interface wireguard peers remove [find comment="VPS YinoWISP Peer"];
/interface wireguard peers add interface=wg-yinowisp endpoint-address="${vpsHost}" endpoint-port=51820 allowed-address=10.8.0.0/24 persistent-keepalive=25s comment="VPS YinoWISP Peer";

# 5. Configurar DNS estatico local para avisos de corte
/ip dns static remove [find name="aviso.yinowisp.com"];
/ip dns static add name="aviso.yinowisp.com" address=10.8.0.1 comment="Servidor de Avisos YinoWISP";

:put "=== WIREGUARD YINOWISP CONFIGURADO EXITOSAMENTE ===";
`;

  const activeScript = tunnelType === 'sstp' ? sstpScript : wireguardScript;

  const handleCopy = () => {
    navigator.clipboard.writeText(activeScript);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
        <CodeIcon color="primary" /> Script de Conexión sin IP Pública — {router.name}
      </DialogTitle>
      <DialogContent divider>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Si este router MikroTik se encuentra detrás de un módem de proveedor, CGNAT o no cuenta con IP Pública, copia este script y pégalo directamente en la terminal de Winbox (<strong>New Terminal</strong>). El router se conectará de inmediato con YinoWISP.
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Selecciona el Tipo de Túnel:
            </Typography>
            <RadioGroup
              row
              value={tunnelType}
              onChange={(e) => setTunnelType(e.target.value)}
            >
              <FormControlLabel value="sstp" control={<Radio size="small" />} label="SSTP (Recomendado / Puerto 443 HTTPS)" />
              <FormControlLabel value="wireguard" control={<Radio size="small" />} label="WireGuard (UDP 51820)" />
            </RadioGroup>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: '#0f172a',
              color: '#38bdf8',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              borderRadius: 2,
              maxHeight: '320px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}
          >
            {activeScript}
          </Paper>

          {copied && (
            <Alert severity="success" sx={{ py: 0.5 }}>
              ¡Script copiado al portapapeles! Ahora puedes pegarlo en Winbox ➔ New Terminal.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit">
          Cerrar
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          color="primary"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
        >
          {copied ? '¡Copiado!' : 'Copiar Script para Winbox'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


const RouterList = () => {
  const { user } = useAuth();
  const [routers, setRouters] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [vpnConfirmOpen, setVpnConfirmOpen] = useState(false);
  const [restartingVpn, setRestartingVpn] = useState(false);
  const [vpnError, setVpnError] = useState('');
  const [vpnSuccess, setVpnSuccess] = useState('');

  const handleRestartWireGuard = async () => {
    setRestartingVpn(true);
    setVpnError('');
    setVpnSuccess('');
    try {
      const response = await fetch('/api/network/wireguard/restart', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al reiniciar el túnel VPN');
      }
      setVpnSuccess(data.message || 'Túnel WireGuard reiniciado exitosamente.');
      setTimeout(() => {
        setVpnConfirmOpen(false);
        setVpnSuccess('');
        fetchInitialData();
      }, 3000);
    } catch (error) {
      console.error('Error al reiniciar WireGuard:', error);
      setVpnError(error.message || 'Error al intentar reiniciar el túnel VPN');
    } finally {
      setRestartingVpn(false);
    }
  };
  const [openForm, setOpenForm] = useState(false);
  const [selectedRouter, setSelectedRouter] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [routerToDelete, setRouterToDelete] = useState(null);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [routerForBroadcast, setRouterForBroadcast] = useState(null);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [routerForSetup, setRouterForSetup] = useState(null);
  const [monitorDialogOpen, setMonitorDialogOpen] = useState(false);
  const [routerForMonitor, setRouterForMonitor] = useState(null);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [routerForScript, setRouterForScript] = useState(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuActiveRow, setMenuActiveRow] = useState(null);
  const [rebootConfirmOpen, setRebootConfirmOpen] = useState(false);
  const [routerToReboot, setRouterToReboot] = useState(null);
  const [rebooting, setRebooting] = useState(false);

  const handleMenuOpen = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuActiveRow(row);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuActiveRow(null);
  };

  const handleReboot = async (id) => {
    setRebooting(true);
    try {
      const response = await fetch(`/api/routers/${id}/reboot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al reiniciar el router');
      alert(data.message || 'El router se está reiniciando...');
      setRebootConfirmOpen(false);
      setRouterToReboot(null);
    } catch (error) {
      console.error('Error rebooting router:', error);
      alert(error.message || 'Error al intentar reiniciar el router');
    } finally {
      setRebooting(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [routersRes, zonesRes] = await Promise.all([
        fetch('/api/routers', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/zones', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const [routersData, zonesData] = await Promise.all([
        routersRes.json(),
        zonesRes.json()
      ]);

      setRouters(routersData);
      setZones(zonesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const method = selectedRouter ? 'PUT' : 'POST';
      const url = selectedRouter
        ? `/api/routers/${selectedRouter.id}`
        : '/api/routers';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Error al guardar el router');

      fetchInitialData();
      setOpenForm(false);
      setSelectedRouter(null);
    } catch (error) {
      console.error('Error saving router:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/routers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Error al eliminar el router');

      fetchInitialData();
      setDeleteConfirmOpen(false);
      setRouterToDelete(null);
    } catch (error) {
      console.error('Error deleting router:', error);
    }
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'Nombre', 
      flex: 1.2, 
      minWidth: 140 
    },
    {
      field: 'connection',
      headerName: 'Conexión API',
      width: 180,
      renderCell: (params) => {
        const type = params.row.api_type || 'classic';
        const label = type === 'rest' ? 'REST' : type === 'rest-ssl' ? 'REST SSL' : 'Clásica';
        const color = type === 'rest' ? 'info' : type === 'rest-ssl' ? 'success' : 'default';
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontFamily: 'monospace', fontSize: '0.825rem' }}>
              {params.row.ip}:{params.row.api_port}
            </Typography>
            <Typography variant="caption" sx={{ 
              fontWeight: 700,
              color: color === 'success' ? '#059669' : color === 'info' ? '#0284c7' : '#64748b',
              fontSize: '0.675rem'
            }}>
              API: {label}
            </Typography>
          </Box>
        );
      }
    },
    { field: 'username', headerName: 'Usuario', width: 100 },
    { field: 'ip_subnets', headerName: 'Subredes', width: 140 },
    { field: 'client_interface', headerName: 'Interfaz', width: 100 },
    {
      field: 'zone_name',
      headerName: 'Zona',
      width: 130,
      valueGetter: (params) => {
        const zone = zones.find(z => z.id === params.row.zone_id);
        return zone ? zone.name : '';
      }
    },
    {
      field: 'clientCount',
      headerName: 'Clientes',
      width: 90,
      type: 'number'
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRouter(params.row);
                setOpenForm(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setRouterToDelete(params.row);
                setDeleteConfirmOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuOpen(e, params.row);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      {/* Cabecera Premium */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Routers
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
            Administre, configure y supervise los routers MikroTik de la red.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
          {user?.role === 'admin' && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RestartIcon />}
              onClick={() => {
                setVpnError('');
                setVpnSuccess('');
                setVpnConfirmOpen(true);
              }}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                py: 1.2,
                borderWidth: '2px',
                borderColor: '#f97316',
                color: '#f97316',
                '&:hover': {
                  borderWidth: '2px',
                  borderColor: '#ea580c',
                  backgroundColor: 'rgba(249, 115, 22, 0.04)'
                },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Reiniciar VPN / WireGuard
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedRouter(null);
              setOpenForm(true);
            }}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              py: 1.2,
              backgroundColor: '#6366f1',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
              '&:hover': { backgroundColor: '#4f46e5' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Nuevo Router
          </Button>
        </Box>
      </Box>

      {/* Tabla Premium */}
      <Paper
        sx={{
          borderRadius: '24px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
          backgroundColor: 'white',
          p: 1
        }}
      >
        <DataGrid
          rows={routers}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          sx={{
            border: 'none',
            fontFamily: 'Inter, sans-serif',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#475569',
                fontSize: '0.85rem'
              }
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.04)',
              color: '#334155',
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                transform: 'scale(1.002)',
              }
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
              }
            },
          }}
        />
      </Paper>

      <RouterForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedRouter(null);
        }}
        onSubmit={handleSubmit}
        router={selectedRouter}
        zones={zones}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar el router "{routerToDelete?.name}"?
          {routerToDelete?.clientCount > 0 && (
            <Typography color="error" className="mt-2">
              ¡Advertencia! Este router tiene {routerToDelete.clientCount} clientes asignados.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => handleDelete(routerToDelete.id)}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <BroadcastDialog
        open={broadcastDialogOpen}
        onClose={() => {
          setBroadcastDialogOpen(false);
          setRouterForBroadcast(null);
        }}
        router={routerForBroadcast}
      />

      <RouterSetupDialog
        open={setupDialogOpen}
        onClose={() => {
          setSetupDialogOpen(false);
          setRouterForSetup(null);
        }}
        router={routerForSetup}
      />

      <RouterMonitorModal
        open={monitorDialogOpen}
        onClose={() => {
          setMonitorDialogOpen(false);
          setRouterForMonitor(null);
        }}
        router={routerForMonitor}
      />

      {/* Menú de Acciones Adicionales */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          setRouterForMonitor(menuActiveRow);
          setMonitorDialogOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <MonitorIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>Monitorear Router</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          setRouterForSetup(menuActiveRow);
          setSetupDialogOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ConstructionIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText>Auto-Configurar Reglas</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          setRouterForScript(menuActiveRow);
          setScriptDialogOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <CodeIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Script de Conexión (Sin IP Pública)</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          setRouterForBroadcast(menuActiveRow);
          setBroadcastDialogOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <CampaignIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Enviar Difusión</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          setRouterToReboot(menuActiveRow);
          setRebootConfirmOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <RestartIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Reiniciar Router</ListItemText>
        </MenuItem>
      </Menu>

      {/* Diálogo de Generación de Script para Routers sin IP Pública */}
      <RouterNoIpScriptDialog
        open={scriptDialogOpen}
        onClose={() => setScriptDialogOpen(false)}
        router={routerForScript}
      />

      {/* Diálogo de Confirmación de Reinicio */}
      <Dialog
        open={rebootConfirmOpen}
        onClose={() => !rebooting && setRebootConfirmOpen(false)}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Confirmar Reinicio de Router</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569', fontWeight: 500 }}>
            ¿Estás seguro de que deseas reiniciar el router "{routerToReboot?.name}" ({routerToReboot?.ip})?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>
            ⚠️ ¡ATENCIÓN! Esta acción interrumpirá temporalmente la conexión a Internet de todos los clientes conectados a este router.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={() => setRebootConfirmOpen(false)}
            disabled={rebooting}
            sx={{ borderRadius: '8px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleReboot(routerToReboot.id)}
            color="error"
            variant="contained"
            disabled={rebooting}
            startIcon={rebooting ? <CircularProgress size={20} color="inherit" /> : <RestartIcon />}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3, fontWeight: 700 }}
          >
            {rebooting ? 'Reiniciando...' : 'Reiniciar Router'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmación de Reinicio de VPN WireGuard */}
      <Dialog
        open={vpnConfirmOpen}
        onClose={() => !restartingVpn && setVpnConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestartIcon color="warning" /> Reiniciar VPN / WireGuard del Servidor
        </DialogTitle>
        <DialogContent dividers>
          <Box className="space-y-4">
            {vpnError && <Alert severity="error">{vpnError}</Alert>}
            {vpnSuccess && <Alert severity="success">{vpnSuccess}</Alert>}
            
            <Typography sx={{ color: '#475569', fontWeight: 500 }}>
              ¿Estás seguro de que deseas reiniciar el servicio WireGuard en el VPS?
            </Typography>
            
            <Typography color="error" variant="body2" sx={{ mt: 1.5, fontWeight: 700, bgcolor: 'rgba(239, 68, 68, 0.05)', p: 2, borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
              ⚠️ ¡ATENCIÓN! Esta acción interrumpirá temporalmente el túnel VPN en el servidor, lo que desconectará a todos los routers vinculados (Miraflores, Nuevo Sullana, etc.) durante aproximadamente 10 a 30 segundos. Las conexiones de red se restablecerán automáticamente.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={() => setVpnConfirmOpen(false)}
            disabled={restartingVpn}
            sx={{ borderRadius: '8px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRestartWireGuard}
            color="warning"
            variant="contained"
            disabled={restartingVpn}
            startIcon={restartingVpn ? <CircularProgress size={20} color="inherit" /> : <RestartIcon />}
            sx={{ 
              borderRadius: '8px', 
              textTransform: 'none', 
              px: 3, 
              fontWeight: 700,
              backgroundColor: '#f97316',
              '&:hover': { backgroundColor: '#ea580c' }
            }}
          >
            {restartingVpn ? 'Reiniciando VPN...' : 'Confirmar Reinicio'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RouterList;